/**
 * If you are not familiar with React Navigation, refer to the "Fundamentals" guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { DefaultTheme, NavigationContainer, useNavigation } from '@react-navigation/native';
import { createNativeStackNavigator, NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ActivityIndicator, useDripsyTheme, Pressable, View } from 'dripsy';

import * as React from 'react';

import earnApp, { FeatureModule } from '@/features/earn';
import { RootStackParamList, RootStackRoutes, RootTabParamList } from '../types';

import { UtxAppNavigatorEmbed } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import BillPaymentApp from '@/features/bill-payment';
import ExchangeSubAccountApp from '@/features/exchange-sub-account';
import AccountManagementApp from '@/features/account-management';
import { useStore } from 'store/store';
import CardAccountApp from '@/features/card-account';
import { bulidLinkingConfig } from './LinkingConfiguration';
import DevToolsApp from '@/devtools';

export default function Navigation({ apps, initialUrl }: { apps: FeatureModule[]; initialUrl: string | null }) {
  const store = useStore();

  if (!store.users)
    return (
      <View sx={{ pt: 25 }}>
        <ActivityIndicator size={'large'} />
      </View>
    );

  const linkConfig = bulidLinkingConfig(apps);

  return (
    <NavigationContainer
      theme={{
        ...DefaultTheme,
        colors: {
          ...DefaultTheme.colors,
          background: 'transparent'
        }
      }}
      linking={bulidLinkingConfig(apps)}>
      <RootNavigator apps={apps} initialUrl={initialUrl} />
    </NavigationContainer>
  );
}

/**
 * A root stack navigator is often used for displaying modals on top of all other content.
 * https://reactnavigation.org/docs/modal
 */
const Stack = createNativeStackNavigator<RootStackParamList>();

function RootNavigator({ apps, initialUrl }: { apps: FeatureModule[]; initialUrl: string | null }) {
  let routeName = initialUrl ? `${initialUrl.substring(initialUrl.lastIndexOf('/') + 1)}` : '';

  return (
    <UtxAppNavigatorEmbed apps={apps} routeName={routeName}>
      <Stack.Navigator
        screenOptions={{
          headerShown: false
        }}
        initialRouteName={RootStackRoutes.DevTools}>
        <Stack.Screen name={RootStackRoutes.DevTools} component={DevToolsApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.Root} component={DevToolsApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.Earn} component={earnApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.ExchangeSubAccount} component={ExchangeSubAccountApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.AccountManagement} component={AccountManagementApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.CardAccount} component={CardAccountApp.screens.main} />
        <Stack.Screen name={RootStackRoutes.BillPayment} component={BillPaymentApp.screens.main} />
      </Stack.Navigator>
    </UtxAppNavigatorEmbed>
  );
}

/**
 * A bottom tab navigator displays tab buttons on the bottom of the display to switch screens.
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */

function EmptyNavigator() {
  return <View />;
}
